import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  NOTIFICATION_CENTER_DEADLINES_TAB_ID,
  NOTIFICATION_CENTER_PAGE_SIZE,
  NOTIFICATION_CENTER_TAB_KEYS,
  isNotificationCenterPaginatedTable,
} from "./notificationCenterConstants";
import {
  getNotificationCenter,
  getNotificationCenterCounters,
  getNotificationCenterTable,
} from "./notificationCenterApi";
import { getNotificationCenterShowAllQueryParams } from "../../../containers/NotificationCenter/utils/notificationCenterAccess";

function normalizeList(value) {
  if (Array.isArray(value)) return value;
  if (value?.items && Array.isArray(value.items)) return value.items;
  if (value?.data && Array.isArray(value.data)) return value.data;
  if (value?.results && Array.isArray(value.results)) return value.results;
  return [];
}

function createEmptyByTab() {
  return {};
}

function createEmptyByTableCounts() {
  return {};
}

function createEmptyTabTableState(limit = NOTIFICATION_CENTER_PAGE_SIZE) {
  return {
    loading: false,
    error: null,
    default: {
      items: [],
      total: 0,
      offset: 0,
      limit,
    },
  };
}

/**
 * API shape: { total: number, by_table: { reporting?: number, ... } }
 */
export function normalizeNotificationCenterCounters(raw) {
  if (!raw || typeof raw !== "object") {
    return { total: 0, byTable: createEmptyByTableCounts(), allowedTableIds: [] };
  }

  const source =
    raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  const incoming = source.by_table ?? source.byTable ?? {};
  const knownTableIds = Object.keys(incoming).filter((tabId) =>
    NOTIFICATION_CENTER_TAB_KEYS.includes(tabId)
  );
  const byTable = createEmptyByTableCounts();
  for (const tabId of knownTableIds) {
    byTable[tabId] = Number(incoming[tabId]) || 0;
  }
  const allowedTableIds = knownTableIds.filter((tabId) => byTable[tabId] > 0);

  return {
    total: Number(source.total) || 0,
    byTable,
    allowedTableIds,
  };
}

/**
 * GET /notify/notification_center/:table_id
 * { table_id, total, offset, limit, items: Row[] }
 */
export function normalizeNotificationCenterTableResponse(raw, tableId, fallbackOffset, fallbackLimit) {
  const source =
    raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  return {
    tableId: source?.table_id ?? tableId,
    items: normalizeList(source?.items),
    total: Number(source?.total) || 0,
    offset: Number(source?.offset) ?? fallbackOffset,
    limit: Number(source?.limit) ?? fallbackLimit,
  };
}

function isSilentFetch(arg) {
  return Boolean(arg && typeof arg === "object" && arg.silent);
}

function tableRequestKey(tableId, offset, limit, showAll = false) {
  return `${tableId}:${offset}:${limit}:${showAll ? 1 : 0}`;
}

/** Prevents duplicate in-flight GETs for the same table page (e.g. Strict Mode / double effect). */
const inFlightTableRequests = new Set();
let inFlightDeadlinesRequest = false;

/**
 * GET /notify/notification_center?only_active=true
 * Deadlines: { deadlines: Row[] } — client-side pagination (20 per page).
 */
export function extractNotificationCenterDeadlines(raw) {
  const source =
    raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  return normalizeList(source?.deadlines);
}

export function paginateDeadlinesState(
  allItems,
  offset = 0,
  limit = NOTIFICATION_CENTER_PAGE_SIZE
) {
  const total = allItems.length;
  const pageSize = Math.max(1, Number(limit) || NOTIFICATION_CENTER_PAGE_SIZE);
  const maxOffset =
    total > 0 ? Math.max(0, Math.ceil(total / pageSize) - 1) * pageSize : 0;
  const safeOffset = Math.min(Math.max(0, Number(offset) || 0), maxOffset);

  return {
    allItems,
    items: allItems.slice(safeOffset, safeOffset + pageSize),
    total,
    offset: safeOffset,
    limit: pageSize,
  };
}

export const fetchNotificationCenterDeadlines = createAsyncThunk(
  "notificationCenter/fetchDeadlines",
  async ({ silent = false, offset } = {}, { rejectWithValue }) => {
    try {
      const response = await getNotificationCenter();
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const message =
          typeof err?.message === "string"
            ? err.message
            : typeof err?.error === "string"
              ? err.error
              : "Server error";
        throw new Error(message);
      }

      const data = await response.json();
      return {
        tableId: NOTIFICATION_CENTER_DEADLINES_TAB_ID,
        allItems: extractNotificationCenterDeadlines(data),
        offset,
        silent,
      };
    } catch (e) {
      return rejectWithValue({
        tableId: NOTIFICATION_CENTER_DEADLINES_TAB_ID,
        message: e?.message || "Failed to load notification center deadlines",
      });
    }
  },
  {
    condition: () => {
      if (inFlightDeadlinesRequest) {
        return false;
      }
      inFlightDeadlinesRequest = true;
      return true;
    },
  }
);

export const fetchNotificationCenterTable = createAsyncThunk(
  "notificationCenter/fetchTable",
  async (
    {
      tableId,
      offset = 0,
      limit = NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
      showAll = false,
    } = {},
    { rejectWithValue }
  ) => {
    try {
      if (!tableId) {
        throw new Error("tableId is required");
      }
      if (!isNotificationCenterPaginatedTable(tableId)) {
        throw new Error(`tableId "${tableId}" does not support pagination`);
      }

      const response = await getNotificationCenterTable(tableId, {
        offset,
        limit,
        ...getNotificationCenterShowAllQueryParams({ showAll, tableId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const message =
          typeof err?.message === "string"
            ? err.message
            : typeof err?.error === "string"
              ? err.error
              : "Server error";
        throw new Error(message);
      }

      const data = await response.json();
      return {
        ...normalizeNotificationCenterTableResponse(data, tableId, offset, limit),
        silent,
      };
    } catch (e) {
      return rejectWithValue({
        tableId,
        message: e?.message || "Failed to load notification center table",
      });
    }
  },
  {
    condition: ({
      tableId,
      offset = 0,
      limit = NOTIFICATION_CENTER_PAGE_SIZE,
      showAll = false,
    }) => {
      if (!tableId || !isNotificationCenterPaginatedTable(tableId)) {
        return false;
      }
      const key = tableRequestKey(tableId, offset, limit, showAll);
      if (inFlightTableRequests.has(key)) {
        return false;
      }
      inFlightTableRequests.add(key);
      return true;
    },
  }
);

export const fetchNotificationCenterCounters = createAsyncThunk(
  "notificationCenter/fetchCounters",
  async ({ silent = false, showAll = false } = {}, { rejectWithValue }) => {
    try {
      const response = await getNotificationCenterCounters(
        getNotificationCenterShowAllQueryParams({ showAll })
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const message =
          typeof err?.message === "string"
            ? err.message
            : typeof err?.error === "string"
              ? err.error
              : "Server error";
        throw new Error(message);
      }
      const data = await response.json();
      return normalizeNotificationCenterCounters(data);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Failed to load notification center counters"
      );
    }
  }
);

/** Reload active table page and bell/summary counters after read, close, etc. */
export const refreshNotificationCenterAfterAction = createAsyncThunk(
  "notificationCenter/refreshAfterAction",
  async (arg = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState().notificationCenter;
      const tableId = arg.tableId ?? state.lastFetchedTableId;
      const tabSection = tableId ? state.dataByTab[tableId]?.default : null;
      const showAll = Boolean(state.showAllReporting);

      const tasks = [
        dispatch(fetchNotificationCenterCounters({ silent: true, showAll })).unwrap(),
      ];

      if (tableId === NOTIFICATION_CENTER_DEADLINES_TAB_ID) {
        tasks.push(dispatch(fetchNotificationCenterDeadlines({ silent: true })).unwrap());
      } else if (tableId) {
        tasks.push(
          dispatch(
            fetchNotificationCenterTable({
              tableId,
              offset: arg.offset ?? tabSection?.offset ?? 0,
              limit: arg.limit ?? tabSection?.limit ?? NOTIFICATION_CENTER_PAGE_SIZE,
              silent: true,
              showAll,
            })
          ).unwrap()
        );
      }

      await Promise.all(tasks);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Failed to refresh notification center"
      );
    }
  }
);

const initialState = {
  dataByTab: createEmptyByTab(),
  lastFetchedTableId: null,
  counters: normalizeNotificationCenterCounters(null),
  countersLoading: false,
  countersError: null,
  countersHydrated: false,
  showAllReporting: false,
};

const notificationCenterSlice = createSlice({
  name: "notificationCenter",
  initialState,
  reducers: {
    clearNotificationCenterError: (state, action) => {
      const tableId = action.payload?.tableId ?? state.lastFetchedTableId;
      if (tableId && state.dataByTab[tableId]) {
        state.dataByTab[tableId].error = null;
        return;
      }
      for (const tabId of Object.keys(state.dataByTab)) {
        if (state.dataByTab[tabId]) {
          state.dataByTab[tabId].error = null;
        }
      }
    },
    setNotificationCenterDeadlinesPage: (state, action) => {
      const tableId = NOTIFICATION_CENTER_DEADLINES_TAB_ID;
      const section = state.dataByTab[tableId]?.default;
      if (!section?.allItems) {
        return;
      }

      const offset = Number(action.payload?.offset) || 0;
      state.dataByTab[tableId].default = paginateDeadlinesState(
        section.allItems,
        offset,
        section.limit ?? NOTIFICATION_CENTER_PAGE_SIZE
      );
    },
    setNotificationCenterShowAllReporting: (state, action) => {
      state.showAllReporting = Boolean(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationCenterTable.pending, (state, action) => {
        const { tableId, limit } = action.meta.arg ?? {};
        if (!tableId) return;

        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState(limit);
        }

        if (!isSilentFetch(action.meta?.arg)) {
          state.dataByTab[tableId].loading = true;
        }
        state.dataByTab[tableId].error = null;
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterTable.fulfilled, (state, action) => {
        const { tableId, items, total, offset, limit } = action.payload;
        const showAll = Boolean(action.meta?.arg?.showAll);
        inFlightTableRequests.delete(tableRequestKey(tableId, offset, limit, showAll));
        state.dataByTab[tableId] = {
          loading: false,
          error: null,
          default: {
            items,
            total,
            offset,
            limit,
          },
        };
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterDeadlines.pending, (state, action) => {
        const tableId = NOTIFICATION_CENTER_DEADLINES_TAB_ID;
        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState();
        }
        if (!isSilentFetch(action.meta?.arg)) {
          state.dataByTab[tableId].loading = true;
        }
        state.dataByTab[tableId].error = null;
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterDeadlines.fulfilled, (state, action) => {
        inFlightDeadlinesRequest = false;
        const { tableId, allItems, offset: requestedOffset } = action.payload;
        const prevOffset = state.dataByTab[tableId]?.default?.offset ?? 0;
        const offset =
          requestedOffset !== undefined ? requestedOffset : prevOffset;

        state.dataByTab[tableId] = {
          loading: false,
          error: null,
          default: paginateDeadlinesState(allItems, offset, NOTIFICATION_CENTER_PAGE_SIZE),
        };
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterDeadlines.rejected, (state, action) => {
        inFlightDeadlinesRequest = false;
        const tableId =
          action.payload?.tableId ?? NOTIFICATION_CENTER_DEADLINES_TAB_ID;
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load notification center deadlines";

        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState();
        }
        state.dataByTab[tableId].loading = false;
        state.dataByTab[tableId].error = message;
      })
      .addCase(fetchNotificationCenterTable.rejected, (state, action) => {
        const tableId = action.payload?.tableId ?? action.meta?.arg?.tableId;
        const {
          offset = 0,
          limit = NOTIFICATION_CENTER_PAGE_SIZE,
          showAll = false,
        } = action.meta?.arg ?? {};
        if (tableId) {
          inFlightTableRequests.delete(tableRequestKey(tableId, offset, limit, showAll));
        }
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load notification center table";

        if (tableId) {
          if (!state.dataByTab[tableId]) {
            state.dataByTab[tableId] = createEmptyTabTableState();
          }
          state.dataByTab[tableId].loading = false;
          state.dataByTab[tableId].error = message;
        }
      })
      .addCase(fetchNotificationCenterCounters.pending, (state, action) => {
        if (!isSilentFetch(action.meta?.arg)) {
          state.countersLoading = true;
        }
        state.countersError = null;
      })
      .addCase(fetchNotificationCenterCounters.fulfilled, (state, action) => {
        state.countersLoading = false;
        state.counters = action.payload ?? normalizeNotificationCenterCounters(null);
        state.countersHydrated = true;
      })
      .addCase(fetchNotificationCenterCounters.rejected, (state, action) => {
        state.countersLoading = false;
        state.countersError =
          action.payload || action.error?.message || "Failed to load counters";
      });
  },
});

export const {
  clearNotificationCenterError,
  setNotificationCenterDeadlinesPage,
  setNotificationCenterShowAllReporting,
} = notificationCenterSlice.actions;
export default notificationCenterSlice.reducer;
